// src/pages/Inventory/AdjustInventoryModal.js
import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { inventoryService } from '../../services/api';
import { toast } from 'react-toastify';

const AdjustInventoryModal = ({ isOpen, onClose, onSuccess, partTypes, products, inventory }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPartType, setSelectedPartType] = useState(null);
    const [currentInventory, setCurrentInventory] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
        setError,
        clearErrors,
    } = useForm();

    const watchPartTypeId = watch('part_type_id');
    const watchProductId = watch('product_id');

    // Reset form when modal is opened or closed
    useEffect(() => {
        if (isOpen) {
            reset();
            setSelectedPartType(null);
            setCurrentInventory(null);
        }
    }, [isOpen, reset]);

    // Watch for part type changes to determine if it's SKU-specific
    useEffect(() => {
        if (watchPartTypeId) {
            const partType = partTypes.find(pt => pt.id === parseInt(watchPartTypeId));
            setSelectedPartType(partType);

            // If switching from SKU-specific to non-SKU-specific, clear product_id
            if (partType && !partType.is_sku_specific) {
                setValue('product_id', '');
            }

            // Reset current inventory
            setCurrentInventory(null);
        } else {
            setSelectedPartType(null);
            setCurrentInventory(null);
        }
    }, [watchPartTypeId, partTypes, setValue]);

    // Update current inventory based on selection
    useEffect(() => {
        if (selectedPartType && inventory) {
            if (selectedPartType.is_sku_specific) {
                // For SKU-specific inventory
                if (watchProductId) {
                    const skuInventory = inventory.sku_specific_inventory.find(
                        item => item.part_type_id === parseInt(watchPartTypeId) &&
                                item.product_id === parseInt(watchProductId)
                    );

                    if (skuInventory) {
                        setCurrentInventory(skuInventory);
                        setValue('new_quantity', skuInventory.current_stock);
                    } else {
                        setCurrentInventory(null);
                        setValue('new_quantity', 0);
                    }
                }
            } else {
                // For general inventory
                const generalInventory = inventory.general_inventory.find(
                    item => item.part_type_id === parseInt(watchPartTypeId)
                );

                if (generalInventory) {
                    setCurrentInventory(generalInventory);
                    setValue('new_quantity', generalInventory.current_stock);
                } else {
                    setCurrentInventory(null);
                    setValue('new_quantity', 0);
                }
            }
        }
    }, [watchPartTypeId, watchProductId, selectedPartType, inventory, setValue]);

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            // Check if notes are provided (required)
            if (!data.notes || data.notes.trim() === '') {
                setError('notes', {
                    type: 'manual',
                    message: 'Notes are required for inventory adjustments'
                });
                setIsLoading(false);
                return;
            }

            // Convert string values to numbers
            data.part_type_id = parseInt(data.part_type_id);
            data.new_quantity = parseInt(data.new_quantity);

            if (data.product_id) {
                data.product_id = parseInt(data.product_id);
            }

            await inventoryService.adjustInventory(data);
            toast.success('Inventory adjusted successfully');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error adjusting inventory:', error);
            toast.error(error.response?.data?.message || 'Failed to adjust inventory');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>

                            <div>
                                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                        Adjust Inventory
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            Adjust inventory to a specific quantity. Please provide a reason for the adjustment.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
                                <div>
                                    <label htmlFor="part_type_id" className="block text-sm font-medium text-gray-700">
                                        Part Type *
                                    </label>
                                    <select
                                        id="part_type_id"
                                        {...register('part_type_id', { required: 'Part type is required' })}
                                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                            errors.part_type_id ? 'border-red-300' : ''
                                        }`}
                                    >
                                        <option value="">Select a part type</option>
                                        {partTypes.map((partType) => (
                                            <option key={partType.id} value={partType.id}>
                                                {partType.name} {partType.is_sku_specific ? '(SKU-specific)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.part_type_id && (
                                        <p className="mt-1 text-sm text-red-600">{errors.part_type_id.message}</p>
                                    )}
                                </div>

                                {selectedPartType && selectedPartType.is_sku_specific && (
                                    <div>
                                        <label htmlFor="product_id" className="block text-sm font-medium text-gray-700">
                                            Product SKU *
                                        </label>
                                        <select
                                            id="product_id"
                                            {...register('product_id', {
                                                required: selectedPartType?.is_sku_specific ? 'Product SKU is required for SKU-specific parts' : false
                                            })}
                                            className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                                errors.product_id ? 'border-red-300' : ''
                                            }`}
                                        >
                                            <option value="">Select a product SKU</option>
                                            {products.map((product) => (
                                                <option key={product.id} value={product.id}>
                                                    {product.sku}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.product_id && (
                                            <p className="mt-1 text-sm text-red-600">{errors.product_id.message}</p>
                                        )}
                                    </div>
                                )}

                                {currentInventory && (
                                    <div className="rounded-md bg-blue-50 p-4">
                                        <div className="flex">
                                            <div className="ml-3 flex-1 md:flex md:justify-between">
                                                <p className="text-sm text-blue-700">
                                                    Current inventory: <span className="font-medium">{currentInventory.current_stock}</span> units
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="new_quantity" className="block text-sm font-medium text-gray-700">
                                        New Quantity *
                                    </label>
                                    <input
                                        type="number"
                                        id="new_quantity"
                                        {...register('new_quantity', {
                                            required: 'New quantity is required',
                                            min: { value: 0, message: 'Quantity must be at least 0' }
                                        })}
                                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                            errors.new_quantity ? 'border-red-300' : ''
                                        }`}
                                        placeholder="Enter new quantity"
                                    />
                                    {errors.new_quantity && (
                                        <p className="mt-1 text-sm text-red-600">{errors.new_quantity.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                        Notes (Required) *
                                    </label>
                                    <textarea
                                        id="notes"
                                        rows={3}
                                        {...register('notes', {
                                            required: 'Notes are required for inventory adjustments'
                                        })}
                                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                            errors.notes ? 'border-red-300' : ''
                                        }`}
                                        placeholder="Explain the reason for this inventory adjustment"
                                    />
                                    {errors.notes && (
                                        <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
                                    )}
                                </div>

                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                                    >
                                        {isLoading ? 'Processing...' : 'Adjust Inventory'}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default AdjustInventoryModal;
